/**
 * SAFECARE Calculator - Core Logic (Per-Person Model)
 * Implements cost calculations for current system vs SAFECARE
 * CRITICAL: SAFECARE is per-person, not per-household (§203)
 */

/**
 * State presets for typical employer plans
 * Values are annual costs based on KFF 2023 data
 */
export const STATE_PRESETS = {
    AL: { name: "Alabama", avgEmployerPremium: 8000, avgEmployeePremium: 2500, avgOOP: 1500 },
    AK: { name: "Alaska", avgEmployerPremium: 9500, avgEmployeePremium: 3200, avgOOP: 1800 },
    AZ: { name: "Arizona", avgEmployerPremium: 8200, avgEmployeePremium: 2600, avgOOP: 1500 },
    AR: { name: "Arkansas", avgEmployerPremium: 7800, avgEmployeePremium: 2400, avgOOP: 1400 },
    CA: { name: "California", avgEmployerPremium: 9000, avgEmployeePremium: 3300, avgOOP: 2000 },
    CO: { name: "Colorado", avgEmployerPremium: 8800, avgEmployeePremium: 3000, avgOOP: 1800 },
    CT: { name: "Connecticut", avgEmployerPremium: 9600, avgEmployeePremium: 3400, avgOOP: 2000 },
    DE: { name: "Delaware", avgEmployerPremium: 8700, avgEmployeePremium: 2900, avgOOP: 1700 },
    FL: { name: "Florida", avgEmployerPremium: 8300, avgEmployeePremium: 2700, avgOOP: 1600 },
    GA: { name: "Georgia", avgEmployerPremium: 8200, avgEmployeePremium: 2600, avgOOP: 1600 },
    HI: { name: "Hawaii", avgEmployerPremium: 7800, avgEmployeePremium: 2200, avgOOP: 1300 },
    ID: { name: "Idaho", avgEmployerPremium: 7900, avgEmployeePremium: 2400, avgOOP: 1400 },
    IL: { name: "Illinois", avgEmployerPremium: 8800, avgEmployeePremium: 3000, avgOOP: 1800 },
    IN: { name: "Indiana", avgEmployerPremium: 8400, avgEmployeePremium: 2700, avgOOP: 1600 },
    IA: { name: "Iowa", avgEmployerPremium: 8300, avgEmployeePremium: 2600, avgOOP: 1500 },
    KS: { name: "Kansas", avgEmployerPremium: 8200, avgEmployeePremium: 2600, avgOOP: 1500 },
    KY: { name: "Kentucky", avgEmployerPremium: 8000, avgEmployeePremium: 2500, avgOOP: 1500 },
    LA: { name: "Louisiana", avgEmployerPremium: 8200, avgEmployeePremium: 2600, avgOOP: 1600 },
    ME: { name: "Maine", avgEmployerPremium: 8600, avgEmployeePremium: 2900, avgOOP: 1700 },
    MD: { name: "Maryland", avgEmployerPremium: 9100, avgEmployeePremium: 3200, avgOOP: 1900 },
    MA: { name: "Massachusetts", avgEmployerPremium: 9600, avgEmployeePremium: 3600, avgOOP: 2100 },
    MI: { name: "Michigan", avgEmployerPremium: 8400, avgEmployeePremium: 2700, avgOOP: 1600 },
    MN: { name: "Minnesota", avgEmployerPremium: 8700, avgEmployeePremium: 2900, avgOOP: 1700 },
    MS: { name: "Mississippi", avgEmployerPremium: 7800, avgEmployeePremium: 2400, avgOOP: 1400 },
    MO: { name: "Missouri", avgEmployerPremium: 8300, avgEmployeePremium: 2600, avgOOP: 1600 },
    MT: { name: "Montana", avgEmployerPremium: 8000, avgEmployeePremium: 2500, avgOOP: 1500 },
    NE: { name: "Nebraska", avgEmployerPremium: 8400, avgEmployeePremium: 2700, avgOOP: 1600 },
    NV: { name: "Nevada", avgEmployerPremium: 8200, avgEmployeePremium: 2600, avgOOP: 1600 },
    NH: { name: "New Hampshire", avgEmployerPremium: 9100, avgEmployeePremium: 3200, avgOOP: 1900 },
    NJ: { name: "New Jersey", avgEmployerPremium: 9800, avgEmployeePremium: 3500, avgOOP: 2100 },
    NM: { name: "New Mexico", avgEmployerPremium: 8000, avgEmployeePremium: 2500, avgOOP: 1500 },
    NY: { name: "New York", avgEmployerPremium: 9700, avgEmployeePremium: 3500, avgOOP: 2100 },
    NC: { name: "North Carolina", avgEmployerPremium: 8400, avgEmployeePremium: 2700, avgOOP: 1600 },
    ND: { name: "North Dakota", avgEmployerPremium: 8300, avgEmployeePremium: 2600, avgOOP: 1500 },
    OH: { name: "Ohio", avgEmployerPremium: 8400, avgEmployeePremium: 2700, avgOOP: 1600 },
    OK: { name: "Oklahoma", avgEmployerPremium: 8000, avgEmployeePremium: 2500, avgOOP: 1500 },
    OR: { name: "Oregon", avgEmployerPremium: 8600, avgEmployeePremium: 2900, avgOOP: 1700 },
    PA: { name: "Pennsylvania", avgEmployerPremium: 8800, avgEmployeePremium: 3000, avgOOP: 1800 },
    RI: { name: "Rhode Island", avgEmployerPremium: 9100, avgEmployeePremium: 3200, avgOOP: 1900 },
    SC: { name: "South Carolina", avgEmployerPremium: 8200, avgEmployeePremium: 2600, avgOOP: 1600 },
    SD: { name: "South Dakota", avgEmployerPremium: 8200, avgEmployeePremium: 2600, avgOOP: 1500 },
    TN: { name: "Tennessee", avgEmployerPremium: 8200, avgEmployeePremium: 2600, avgOOP: 1600 },
    TX: { name: "Texas", avgEmployerPremium: 8400, avgEmployeePremium: 2800, avgOOP: 1700 },
    UT: { name: "Utah", avgEmployerPremium: 8000, avgEmployeePremium: 2500, avgOOP: 1500 },
    VT: { name: "Vermont", avgEmployerPremium: 9000, avgEmployeePremium: 3200, avgOOP: 1900 },
    VA: { name: "Virginia", avgEmployerPremium: 8800, avgEmployeePremium: 3000, avgOOP: 1800 },
    WA: { name: "Washington", avgEmployerPremium: 8800, avgEmployeePremium: 3000, avgOOP: 1800 },
    WV: { name: "West Virginia", avgEmployerPremium: 8000, avgEmployeePremium: 2500, avgOOP: 1500 },
    WI: { name: "Wisconsin", avgEmployerPremium: 8600, avgEmployeePremium: 2900, avgOOP: 1700 },
    WY: { name: "Wyoming", avgEmployerPremium: 8200, avgEmployeePremium: 2600, avgOOP: 1600 }
};

/**
 * Insurance plan type presets
 * Based on 2024 KFF Employer Health Benefits Survey and marketplace data
 */
export const INSURANCE_PLAN_PRESETS = {
    HDHP_INDIVIDUAL: {
        label: "High-Deductible (HDHP) - Individual",
        description: "$7,500 deductible, HSA-eligible",
        type: "individual",
        employerPremium: 6200,
        employeePremium: 1400,
        deductible: 7500,
        oopMax: 8000,
        expectedOOP: 3500
    },
    HDHP_FAMILY: {
        label: "High-Deductible (HDHP) - Family",
        description: "$15,000 family deductible, HSA-eligible",
        type: "family",
        employerPremium: 17500,
        employeePremium: 4200,
        deductible: 15000,
        oopMax: 16000,
        expectedOOP: 7000
    },
    PPO_STANDARD_INDIVIDUAL: {
        label: "PPO Standard - Individual",
        description: "$1,500 deductible, broad network",
        type: "individual",
        employerPremium: 7800,
        employeePremium: 2200,
        deductible: 1500,
        oopMax: 6000,
        expectedOOP: 2000
    },
    PPO_STANDARD_FAMILY: {
        label: "PPO Standard - Family",
        description: "$3,000 family deductible, broad network",
        type: "family",
        employerPremium: 22000,
        employeePremium: 6500,
        deductible: 3000,
        oopMax: 12000,
        expectedOOP: 4500
    },
    HMO_INDIVIDUAL: {
        label: "HMO - Individual",
        description: "$500 deductible, PCP required",
        type: "individual",
        employerPremium: 7200,
        employeePremium: 1800,
        deductible: 500,
        oopMax: 5000,
        expectedOOP: 1500
    },
    HMO_FAMILY: {
        label: "HMO - Family",
        description: "$1,000 family deductible, PCP required",
        type: "family",
        employerPremium: 20000,
        employeePremium: 5500,
        deductible: 1000,
        oopMax: 10000,
        expectedOOP: 3500
    },
    EPO_INDIVIDUAL: {
        label: "EPO - Individual",
        description: "$1,000 deductible, no out-of-network",
        type: "individual",
        employerPremium: 7500,
        employeePremium: 2000,
        deductible: 1000,
        oopMax: 5500,
        expectedOOP: 1800
    },
    EPO_FAMILY: {
        label: "EPO - Family",
        description: "$2,000 family deductible, no out-of-network",
        type: "family",
        employerPremium: 21000,
        employeePremium: 6000,
        deductible: 2000,
        oopMax: 11000,
        expectedOOP: 4000
    },
    PREMIUM_EMPLOYER_INDIVIDUAL: {
        label: "Premium Employer Plan - Individual",
        description: "$0 deductible, top-tier coverage",
        type: "individual",
        employerPremium: 9500,
        employeePremium: 800,
        deductible: 0,
        oopMax: 3000,
        expectedOOP: 800
    },
    PREMIUM_EMPLOYER_FAMILY: {
        label: "Premium Employer Plan - Family",
        description: "$0 deductible, top-tier coverage",
        type: "family",
        employerPremium: 26000,
        employeePremium: 2500,
        deductible: 0,
        oopMax: 6000,
        expectedOOP: 2000
    },
    ACA_BRONZE_INDIVIDUAL: {
        label: "ACA Bronze (Marketplace) - Individual",
        description: "$6,850 deductible, 60% actuarial value",
        type: "individual",
        employerPremium: 0,
        employeePremium: 7200,
        deductible: 6850,
        oopMax: 9450,
        expectedOOP: 4500
    },
    ACA_SILVER_INDIVIDUAL: {
        label: "ACA Silver (Marketplace) - Individual",
        description: "$4,500 deductible, 70% actuarial value",
        type: "individual",
        employerPremium: 0,
        employeePremium: 8500,
        deductible: 4500,
        oopMax: 9450,
        expectedOOP: 3500
    },
    ACA_GOLD_INDIVIDUAL: {
        label: "ACA Gold (Marketplace) - Individual",
        description: "$1,500 deductible, 80% actuarial value",
        type: "individual",
        employerPremium: 0,
        employeePremium: 10200,
        deductible: 1500,
        oopMax: 9450,
        expectedOOP: 2500
    },
    CATASTROPHIC_INDIVIDUAL: {
        label: "Catastrophic Plan - Individual",
        description: "Under 30 or hardship exemption only",
        type: "individual",
        employerPremium: 0,
        employeePremium: 4800,
        deductible: 9450,
        oopMax: 9450,
        expectedOOP: 5000
    },
    MEDICARE_TRADITIONAL: {
        label: "Medicare (Traditional)",
        description: "Parts A & B, no supplemental",
        type: "individual",
        employerPremium: 0,
        employeePremium: 1850, // Part B premium ~$174/month
        deductible: 1600, // Part B deductible
        oopMax: 999999, // No cap without supplemental
        expectedOOP: 3500 // Varies widely
    },
    MEDICARE_ADVANTAGE: {
        label: "Medicare Advantage (Part C)",
        description: "All-in-one alternative to Traditional",
        type: "individual",
        employerPremium: 0,
        employeePremium: 2400, // Part B + plan premium
        deductible: 500,
        oopMax: 8300,
        expectedOOP: 2000
    },
    MEDICAID: {
        label: "Medicaid",
        description: "State program for low-income",
        type: "individual",
        employerPremium: 0,
        employeePremium: 0,
        deductible: 0,
        oopMax: 0,
        expectedOOP: 50 // Minimal copays
    }
};

/**
 * Baseline comparison modes for current system
 */
export const BASELINE_MODES = {
    STATE_AVERAGE: 'STATE_AVERAGE',
    EMPLOYER_PLAN: 'EMPLOYER_PLAN',
    RETIREE_MEDICARE_LIKE: 'RETIREE_MEDICARE_LIKE',
    UNINSURED_OOP: 'UNINSURED_OOP'
};

/**
 * Healthcare usage profiles for estimating copays
 */
export const USAGE_PROFILES = {
    LOW: { label: "Low Use", copayEstimate: 100, description: "Minimal doctor visits" },
    MEDIUM: { label: "Medium Use", copayEstimate: 300, description: "Regular checkups, occasional care" },
    HIGH: { label: "High Use", copayEstimate: 600, description: "Chronic condition or frequent care" },
    CHILD_LOW: { label: "Child (Low)", copayEstimate: 50, description: "Healthy child" },
    CHILD_MEDIUM: { label: "Child (Medium)", copayEstimate: 150, description: "Regular pediatric care" }
};

/**
 * Income source presets for passive and investment income
 * SAFECARE taxes all income sources (§203)
 */
export const INCOME_SOURCE_PRESETS = {
    WAGES_ONLY: {
        label: "Wages Only",
        description: "W-2 employee, no other income",
        earnedIncome: 60000,
        passiveIncome: 0
    },
    WAGES_PLUS_RENTAL: {
        label: "Wages + Rental Income",
        description: "Employee with rental property",
        earnedIncome: 75000,
        passiveIncome: 24000
    },
    WAGES_PLUS_DIVIDENDS: {
        label: "Wages + Investment Dividends",
        description: "Employee with investment portfolio",
        earnedIncome: 90000,
        passiveIncome: 15000
    },
    RETIREMENT_DISTRIBUTIONS: {
        label: "Retirement Distributions",
        description: "Retiree with 401k/IRA withdrawals",
        earnedIncome: 0,
        passiveIncome: 50000
    },
    CAPITAL_GAINS: {
        label: "Capital Gains",
        description: "Investor with stock/property sales",
        earnedIncome: 0,
        passiveIncome: 100000
    },
    MIXED_HIGH_EARNER: {
        label: "High Earner (Mixed Income)",
        description: "Executive with investments",
        earnedIncome: 200000,
        passiveIncome: 80000
    },
    BUSINESS_OWNER: {
        label: "Small Business Owner",
        description: "Self-employed with business income",
        earnedIncome: 120000,
        passiveIncome: 30000
    },
    RETIREE_MODEST: {
        label: "Modest Retiree",
        description: "Social Security + small pension",
        earnedIncome: 0,
        passiveIncome: 35000
    }
};

/**
 * Family situation presets
 * Each defines a household composition with typical incomes
 */
export const FAMILY_SITUATIONS = {
    SINGLE_WORKER: {
        label: "Single, Working",
        description: "One adult with income",
        members: [
            { role: "adult1", age: 35, income: 60000, wages: 60000, passiveIncome: 0, usageProfile: "MEDIUM" }
        ]
    },
    COUPLE_ONE_EARNER: {
        label: "Couple, One Earner",
        description: "Married, one working spouse",
        members: [
            { role: "adult1", age: 40, income: 75000, wages: 75000, passiveIncome: 0, usageProfile: "MEDIUM" },
            { role: "adult2", age: 38, income: 0, wages: 0, passiveIncome: 0, usageProfile: "MEDIUM" }
        ]
    },
    COUPLE_TWO_EARNERS: {
        label: "Couple, Two Earners",
        description: "Married, both working",
        members: [
            { role: "adult1", age: 42, income: 80000, wages: 80000, passiveIncome: 0, usageProfile: "MEDIUM" },
            { role: "adult2", age: 40, income: 50000, wages: 50000, passiveIncome: 0, usageProfile: "MEDIUM" }
        ]
    },
    SINGLE_PARENT_TWO_KIDS: {
        label: "Single Parent + 2 Kids",
        description: "One working parent, two children",
        members: [
            { role: "adult1", age: 38, income: 55000, wages: 55000, passiveIncome: 0, usageProfile: "MEDIUM" },
            { role: "child1", age: 10, income: 0, wages: 0, passiveIncome: 0, usageProfile: "CHILD_LOW" },
            { role: "child2", age: 7, income: 0, wages: 0, passiveIncome: 0, usageProfile: "CHILD_LOW" }
        ]
    },
    FAMILY_TWO_EARNERS_TWO_KIDS: {
        label: "Family (2 Adults + 2 Kids)",
        description: "Dual-earner family with children",
        members: [
            { role: "adult1", age: 42, income: 85000, wages: 85000, passiveIncome: 0, usageProfile: "MEDIUM" },
            { role: "adult2", age: 40, income: 60000, wages: 60000, passiveIncome: 0, usageProfile: "MEDIUM" },
            { role: "child1", age: 12, income: 0, wages: 0, passiveIncome: 0, usageProfile: "CHILD_LOW" },
            { role: "child2", age: 8, income: 0, wages: 0, passiveIncome: 0, usageProfile: "CHILD_MEDIUM" }
        ]
    },
    RETIREE_COUPLE: {
        label: "Retired Couple",
        description: "Two retirees with pensions and Social Security",
        members: [
            { role: "adult1", age: 68, income: 45000, wages: 0, passiveIncome: 45000, usageProfile: "HIGH" },
            { role: "adult2", age: 66, income: 35000, wages: 0, passiveIncome: 35000, usageProfile: "HIGH" }
        ]
    },
    HIGH_EARNER_INVESTMENTS: {
        label: "High Earner with Investments",
        description: "Executive with significant passive income",
        members: [
            { role: "adult1", age: 50, income: 280000, wages: 200000, passiveIncome: 80000, usageProfile: "MEDIUM" },
            { role: "adult2", age: 48, income: 90000, wages: 70000, passiveIncome: 20000, usageProfile: "MEDIUM" }
        ]
    }
};

/**
 * Compute SAFECARE individual contribution based on income
 * Implements §203: 3.5% base + surtaxes at 100k/250k/500k
 * CRITICAL: This is PER PERSON, not household
 * IMPORTANT: Income includes wages + passive/investment income (all Modified AGI)
 * NEW: Retirement income exemption of $100,000 (§203(b)(1))
 */
export function computeSafecareIndividualContribution(person) {
    const income = person.income || 0;
    const retirementIncome = person.retirementIncome || 0;
    const isSelfEmployed = person.isSelfEmployed || false;

    // Apply Retirement Exemption (§203(b)(1)(B)(iv))
    const taxableRetirement = Math.max(0, retirementIncome - 100000);
    const nonRetirementIncome = income - retirementIncome;
    const modifiedAGI = Math.max(0, nonRetirementIncome + taxableRetirement);

    if (modifiedAGI <= 0) return 0;

    // Use 11.5% for Self-Employed (§203(c)(2)) or 3.5% for Wages (§203(c)(1))
    const baseRate = isSelfEmployed ? 0.115 : 0.035;
    let contribution = modifiedAGI * baseRate;

    // Surtaxes (per person thresholds) - applies to both SE and Wages (§203(c)(3)-(4))
    if (modifiedAGI > 100000) {
        const tier = Math.min(modifiedAGI, 250000) - 100000;
        contribution += tier * 0.01;
    }
    if (modifiedAGI > 250000) {
        const tier = Math.min(modifiedAGI, 500000) - 250000;
        contribution += tier * 0.015;
    }
    if (modifiedAGI > 500000) {
        const tier = modifiedAGI - 500000;
        contribution += tier * 0.02;
    }

    return contribution;
}

/**
 * Compute SAFECARE employer contribution
 * §202: 8% of payroll PER WORKER
 */
export function computeSafecareEmployerContribution(person) {
    if (person.isSelfEmployed) return 0; // SE pays 11.5% individual, no separate employer tax
    const annualWages = person.wages || 0;
    if (annualWages <= 0) return 0;
    const employerRate = 0.08;
    return annualWages * employerRate;
}

/**
 * Estimate copays for a person based on usage profile
 * Per-person cap is $750/year
 * Members < 200% FPL have $0 copays (§204(b))
 */
export function estimateSafecareCopays(person) {
    // 200% FPL for single is approx $30,000 (§204(b))
    const fplThreshold = 30000;
    if (person.income < fplThreshold || person.age < 18) {
        return 0;
    }

    const profile = USAGE_PROFILES[person.usageProfile] || USAGE_PROFILES.MEDIUM;
    // Cap at $750 per person (§204(d))
    return Math.min(profile.copayEstimate, 750);
}

/**
 * Compute Health Efficiency Dividend (§202(f))
 * Employer MUST pay back savings to employee as mandatory raise
 */
export function calculateEfficiencyDividend(currentEmployerCost, safecareEmployerCost) {
    return Math.max(0, currentEmployerCost - safecareEmployerCost);
}

/**
 * Compute SAFECARE costs for ONE PERSON
 */
export function computePersonSafecareCosts(person, currentEmployerCost = 0) {
    const individualContribution = Math.round(computeSafecareIndividualContribution(person));
    const employerContribution = Math.round(computeSafecareEmployerContribution(person));
    const copays = Math.round(estimateSafecareCopays(person));

    // Calculate Efficiency Dividend (Potential Raise)
    const efficiencyDividend = calculateEfficiencyDividend(currentEmployerCost, employerContribution);

    return {
        individualContribution,
        employerContribution,
        copays,
        efficiencyDividend,
        totalPersonCost: individualContribution + copays
    };
}

/**
 * Compute SAFECARE costs for HOUSEHOLD (aggregated from individuals)
 */
export function computeHouseholdSafecareCosts(householdMembers, currentEmployerCost = 0) {
    // We attribute current employer cost to the first member for simplicity in the per-person calc,
    // or better, calculate the dividend as a household-level surplus.
    // SEC 202(f) says "for each covered employee", so we should ideally know the per-worker cost.
    // For the simple calculator, we'll calculate the total household dividend.

    const memberResults = householdMembers.map(person => ({
        person,
        costs: computePersonSafecareCosts(person, 0) // Dividend calculated at aggregate level below
    }));

    const totals = memberResults.reduce((acc, { costs }) => ({
        totalIndividualContrib: acc.totalIndividualContrib + costs.individualContribution,
        totalEmployerContrib: acc.totalEmployerContrib + costs.employerContribution,
        totalCopays: acc.totalCopays + costs.copays,
        totalEmployeeCost: acc.totalEmployeeCost + costs.totalPersonCost
    }), {
        totalIndividualContrib: 0,
        totalEmployerContrib: 0,
        totalCopays: 0,
        totalEmployeeCost: 0
    });

    // Calculate aggregate Efficiency Dividend
    const totalEfficiencyDividend = calculateEfficiencyDividend(currentEmployerCost, totals.totalEmployerContrib);

    return {
        memberResults,
        totalEfficiencyDividend,
        ...totals
    };
}

/**
 * Current system costs using insurance plan preset
 */
export function computeCurrentSystemCostsFromPlan(planKey) {
    const plan = INSURANCE_PLAN_PRESETS[planKey];
    if (!plan) throw new Error("Invalid plan preset");

    return {
        employerPremium: plan.employerPremium,
        employeePremium: plan.employeePremium,
        oop: plan.expectedOOP,
        totalEmployeeCost: plan.employeePremium + plan.expectedOOP,
        totalEmployerCost: plan.employerPremium,
        planDetails: {
            deductible: plan.deductible,
            oopMax: plan.oopMax,
            type: plan.type
        }
    };
}

/**
 * Current system costs (household-level for family plans)
 * Legacy function for state-based estimates
 */
export function computeCurrentSystemCosts(stateCode, householdSize, overrides = {}) {
    const state = STATE_PRESETS[stateCode];
    if (!state) throw new Error("Invalid state");

    // Family multipliers
    const familyMultiplier = householdSize === 1 ? 1.0 : 2.8;
    const oopMultiplier = householdSize === 1 ? 1.0 : 2.2;

    const employerPremium = overrides.employerPremium !== undefined
        ? overrides.employerPremium
        : Math.round(state.avgEmployerPremium * familyMultiplier);

    const employeePremium = overrides.employeePremium !== undefined
        ? overrides.employeePremium
        : Math.round(state.avgEmployeePremium * familyMultiplier);

    const oop = overrides.oop !== undefined
        ? overrides.oop
        : Math.round(state.avgOOP * oopMultiplier);

    return {
        employerPremium,
        employeePremium,
        oop,
        totalEmployeeCost: employeePremium + oop,
        totalEmployerCost: employerPremium
    };
}

/**
 * Master comparison: current vs SAFECARE for household
 */
export function compareCurrentVsSafecare({
    stateCode = "TX",
    familySituation = "SINGLE_WORKER",
    householdMembers = null,
    insurancePlan = null,
    overrides = {}
}) {
    // Use provided members or get from preset
    const members = householdMembers || FAMILY_SITUATIONS[familySituation].members;

    // Use insurance plan preset if provided, otherwise use state-based estimate
    const current = insurancePlan
        ? computeCurrentSystemCostsFromPlan(insurancePlan)
        : computeCurrentSystemCosts(stateCode, members.length, overrides);

    const safecare = computeHouseholdSafecareCosts(members, current.totalEmployerCost);

    const employeeSavings = current.totalEmployeeCost - safecare.totalEmployeeCost;
    const employerSavings = current.totalEmployerCost - safecare.totalEmployerContrib;

    // Total Financial Benefit to Household include Employee Savings + The mandatory raise (Dividend)
    const householdFinancialBenefit = employeeSavings + safecare.totalEfficiencyDividend;

    return {
        state: STATE_PRESETS[stateCode]?.name || stateCode,
        situation: FAMILY_SITUATIONS[familySituation]?.label || "Custom",
        householdSize: members.length,
        insurancePlan: insurancePlan ? INSURANCE_PLAN_PRESETS[insurancePlan]?.label : "State Average",

        // Current system
        currentEmployeePremium: current.employeePremium,
        currentEmployeeOOP: current.oop,
        currentEmployeeTotal: current.totalEmployeeCost,
        currentEmployerPremium: current.totalEmployerCost,
        currentPlanDetails: current.planDetails,

        // SAFECARE (aggregated)
        safecareContribution: safecare.totalIndividualContrib,
        safecareCopays: safecare.totalCopays,
        safecareEmployeeTotal: safecare.totalEmployeeCost,
        safecareEmployerContribution: safecare.totalEmployerContrib,
        safecareEfficiencyDividend: safecare.totalEfficiencyDividend,

        // Per-person breakdown
        memberBreakdown: safecare.memberResults,

        // Savings
        employeeSavings,
        employeeSavingsPercent: current.totalEmployeeCost > 0
            ? Math.round((employeeSavings / current.totalEmployeeCost) * 100)
            : 0,
        employerSavings, // This is the net "corporate" savings, which might be 0 due to Dividend redistribution
        employerSavingsPercent: current.totalEmployerCost > 0
            ? Math.round((employerSavings / current.totalEmployerCost) * 100)
            : 0,
        householdFinancialBenefit,
        totalSavings: employeeSavings + employerSavings, // Pure system-wide savings
        totalSavingsPercent: (current.totalEmployeeCost + current.totalEmployerCost) > 0
            ? Math.round(((employeeSavings + employerSavings) / (current.totalEmployeeCost + current.totalEmployerCost)) * 100)
            : 0
    };
}

/**
 * Format currency
 */
export function formatCurrency(amount) {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    }).format(amount);
}

/**
 * Format number with commas
 */
export function formatNumber(num) {
    return new Intl.NumberFormat('en-US').format(num);
}
